import { STUDIO_DISPLAY_SECTIONS } from './studioDisplay';

export interface StudioNavigationItem {
  name: string;
  title: string;
  icon: string;
  section?: string;
  /** 流程步骤序号：侧栏显示编号，页面顶部流程条按此顺序串联。 */
  step?: number;
  relatedRoutes?: string[];
  /** 系统流程类型仅用于定位本店流程，查询和保存仍使用本店设置 id。 */
  processType?: number;
  /** 保留菜单位置，暂停开放业务页面；清除此项即可恢复入口。 */
  disabledReason?: string;
}

export interface StudioNavigationGroup {
  name: string;
  title: string;
  icon: string;
  area: 'business' | 'system';
  en: string;
  description: string;
  children: StudioNavigationItem[];
}

export const SERVICE_PROCESS_NAVIGATION: StudioNavigationItem[] = [
  { name: 'ShootingProcess', title: '拍摄安排', icon: 'ri-camera-line', section: '服务流程', processType: 1 },
  { name: 'MakeupProcess', title: '化妆安排', icon: 'ri-brush-line', section: '服务流程', processType: 2 },
  { name: 'ColorMixingProcess', title: '调色安排', icon: 'ri-palette-line', section: '服务流程', processType: 3 },
  { name: 'FilmSelectionProcess', title: '选片安排', icon: 'ri-image-line', section: '服务流程', processType: 4, disabledReason: '暂未开放' },
  { name: 'ServiceDesignProcess', title: '设计安排', icon: 'ri-pencil-ruler-2-line', section: '服务流程', processType: 5 },
  { name: 'ServiceProofingProcess', title: '看板安排', icon: 'ri-eye-line', section: '服务流程', processType: 6 },
  { name: 'ServiceProductionProcess', title: '制作安排', icon: 'ri-tools-line', section: '服务流程', processType: 7 },
  { name: 'ServiceShippingProcess', title: '寄件安排', icon: 'ri-truck-line', section: '服务流程', processType: 8 },
  { name: 'ServicePickupProcess', title: '取件安排', icon: 'ri-handbag-line', section: '服务流程', processType: 9 },
  { name: 'ServiceRefiningProcess', title: '精修安排', icon: 'ri-magic-line', section: '服务流程', processType: 10 },
  { name: 'ServiceMvProcess', title: '微电影', icon: 'ri-film-line', section: '服务流程', processType: 11 },
  { name: 'ServicePhotoDressProcess', title: '拍照礼服', icon: 'ri-shirt-line', section: '服务流程', processType: 12 },
  { name: 'ServiceWeddingDressProcess', title: '结婚礼服', icon: 'ri-shirt-line', section: '服务流程', processType: 13 },
  { name: 'ServiceWeddingMakeupProcess', title: '结婚化妆', icon: 'ri-brush-line', section: '服务流程', processType: 14 },
  { name: 'ServiceReturnDressProcess', title: '回婚装', icon: 'ri-shirt-line', section: '服务流程', processType: 15 },
  { name: 'ServiceDestinationWeddingProcess', title: '目的地婚礼', icon: 'ri-map-pin-line', section: '服务流程', processType: 17 },
  { name: 'ServiceEngagementDressProcess', title: '订婚装', icon: 'ri-shirt-line', section: '服务流程', processType: 16 },
];

// 重复的服务工作页统一转到在线编辑，保留旧 URL 与订单上下文。
export const SERVICE_EDITOR_ROUTES: Record<string,string> = {
  ServiceDesignProcess: 'OrderDesign',
  ServiceProofingProcess: 'DesignProofing',
  ServiceProductionProcess: 'DesignProduction',
};

// 首页、侧栏、商城模块切换和路由白名单共用；保留原有 URL 与业务页面。
export const STUDIO_NAVIGATION: StudioNavigationGroup[] = [
  {
    name: 'StudioDisplay', title: '门店展示', icon: 'ri-store-2-line', area: 'business',
    en: 'STUDIO SHOWROOM', description: '管理影楼对外展示页的品牌、案例、客片、套餐、产品与推广栏目',
    children: [
      { name: 'StudioDisplayOverview', title: '展示页概览', icon: 'ri-layout-4-line' },
      ...STUDIO_DISPLAY_SECTIONS.filter(section => !section.hidden).map(({ name, title, icon, relatedRoutes, disabledReason }) => ({ name, title, icon, relatedRoutes, disabledReason, section: '展示栏目' })),
      { name: 'StudioDisplayShootingType', title: '拍摄类型', icon: 'ri-camera-lens-line', section: '基础设置' },
      { name: 'StudioDisplayLocations', title: '景点路线', icon: 'ri-route-line', section: '基础设置' },
    ],
  },
  {
    name: 'Customers', title: '客户CRM', icon: 'ri-contacts-book-line', area: 'business',
    en: 'CUSTOMER MANAGEMENT', description: '承接渠道客资、跟进意向客户与管理客户订单',
    children: [
      { name: 'CustomerWorkbench', title: '客资工作台', icon: 'ri-dashboard-line' },
      { name: 'CustomerInsights', title: '客资分析', icon: 'ri-line-chart-line' },
      { name: 'CustomerLeads', title: '意向客资', icon: 'ri-user-follow-line' },
      { name: 'StudioMember', title: '客户档案', icon: 'ri-contacts-book-line' },
      { name: 'Order', title: '客户订单', icon: 'ri-file-list-3-line', relatedRoutes: ['OrderDetail'] },
      { name: 'OrderRecycle', title: '订单回收站', icon: 'ri-delete-bin-line' },
      { name: 'ConsumptionType', title: '消费类型', icon: 'ri-list-settings-line', section: '业务设置' },
      { name: 'DocumentCode', title: '单据编码', icon: 'ri-file-settings-line', section: '业务设置' },
    ],
  },
  {
    name: 'SchedulingSystem', title: '影楼管理系统', icon: 'ri-calendar-schedule-line', area: 'business',
    en: 'STUDIO MANAGEMENT', description: '承接 CRM 订单、安排服务排期与人员，查看财务与经营报表',
    children: [
      { name: 'StudioWorkbench', title: '服务工作台', icon: 'ri-dashboard-line' },
      // CRM 下单后的订单在这里按收款、排期、服务、交付推进；客户全流程是单笔订单的展开页。
      { name: 'StudioOrderCenter', title: '订单中心', icon: 'ri-file-list-2-line', relatedRoutes: ['CustomerJourney', 'ServicePickupProcess', 'ServiceShippingProcess'] },
      { name: 'Scheduling', title: '排期日历', icon: 'ri-calendar-2-line' },
      ...[1,2,3,4,10,11,12,17,13,14].map(type => {
        const item = SERVICE_PROCESS_NAVIGATION.find(item => item.processType === type)!;
        const related = type===13 ? [15,16] : [];
        return {...item, relatedRoutes:SERVICE_PROCESS_NAVIGATION.filter(p=>related.includes(p.processType!)).map(p=>p.name)};
      }),
      { name: 'PaymentRecords', title: '收款记录', icon: 'ri-bank-card-line', section: '财务记录' },
      { name: 'CustomerConsumptionRecords', title: '客户消费记录', icon: 'ri-bill-line', section: '财务记录' },
      { name: 'RefundAndReturnRecords', title: '退单与退款', icon: 'ri-refund-2-line', section: '财务记录' },
      { name: 'DailyReport', title: '日报表', icon: 'ri-file-chart-line', section: '财务报表' },
      { name: 'MonthlyReport', title: '月报表', icon: 'ri-bar-chart-box-line', section: '财务报表' },
      { name: 'AnnualReport', title: '年报表', icon: 'ri-line-chart-line', section: '财务报表' },
      { name: 'AnnualSurplusAnalysis', title: '年余款分析', icon: 'ri-funds-line', section: '财务报表' },
      { name: 'MonthlyProcessReport', title: '月流程报表', icon: 'ri-flow-chart', section: '财务报表' },
      { name: 'MonthlyUnpaidReport', title: '月未缴报表', icon: 'ri-money-cny-circle-line', section: '财务报表' },
      { name: 'StudioBusinessSetup', title: '套系与业务设置', icon: 'ri-settings-4-line', section: '业务设置' },
      { name: 'OddIncomeManagement', title: '零散收入项目', icon: 'ri-wallet-3-line', section: '业务设置' },
      { name: 'StudioProcess', title: '流程与岗位设置', icon: 'ri-flow-chart', section: '排期设置' },
      { name: 'ShootingType', title: '拍摄类型', icon: 'ri-camera-lens-line', section: '排期设置' },
      { name: 'ShootingLevel', title: '拍摄级别', icon: 'ri-medal-line', section: '排期设置' },
      { name: 'ScenicSpot', title: '拍摄景点', icon: 'ri-map-pin-line', section: '排期设置' },
    ],
  },
  {
    name: 'OnlineEditing', title: '在线编辑', icon: 'ri-layout-3-line', area: 'business',
    en: 'ONLINE EDITING', description: '按「设计 → 看样 → 送制作」处理订单，并管理模板与精修看样',
    children: [
      // 订单按这三步依次流转，每步只列需要在这一步处理的订单。
      { name: 'OrderDesign', title: '订单设计', icon: 'ri-pencil-ruler-2-line', section: '设计流程', step: 1, relatedRoutes: ['OrderDesignDetail', 'ServiceDesignProcess'] },
      { name: 'DesignProofing', title: '设计看样', icon: 'ri-eye-2-line', section: '设计流程', step: 2, relatedRoutes: ['ServiceProofingProcess'] },
      { name: 'DesignProduction', title: '送制作', icon: 'ri-truck-line', section: '设计流程', step: 3, relatedRoutes: ['ServiceProductionProcess'] },
      { name: 'DesignOrders', title: '全部订单', icon: 'ri-list-check-3', section: '订单与素材' },
      { name: 'Proofing', title: '精修看样', icon: 'ri-magic-line', section: '订单与素材' },
      { name: 'DesignTemplate', title: '模板管理', icon: 'ri-dashboard-line', section: '订单与素材', relatedRoutes: ['DesignTemplateDetail'] },
    ],
  },
  {
    name: 'Products', title: '产品展示管理', icon: 'ri-shopping-bag-3-line', area: 'business',
    en: 'PRODUCT MANAGEMENT', description: '选购合作工厂产品，管理本店产品资料与产品、套系分类',
    children: [
      { name: 'ProductShowcase', title: '工厂选品商城', icon: 'ri-store-2-line', section: '产品资料' },
      { name: 'Product', title: '本店产品资料', icon: 'ri-box-3-line', section: '产品资料' },
      { name: 'ProductCategory', title: '产品分类', icon: 'ri-price-tag-3-line', section: '资料设置' },
      { name: 'PackageCategory', title: '套系分类', icon: 'ri-folders-line', section: '资料设置' },
      { name: 'MvCategory', title: 'MV类别', icon: 'ri-film-line', section: '资料设置' },
    ],
  },
  {
    name: 'System', title: '系统设置', icon: 'ri-settings-3-line', area: 'system',
    en: 'SETTINGS', description: '管理员工、组织与角色权限',
    children: [
      { name: 'DepartmentManage', title: '部门管理', icon: 'ri-building-4-line' },
      { name: 'StudioJob', title: '职位管理', icon: 'ri-briefcase-4-line' },
      { name: 'RoleManage', title: '角色管理', icon: 'ri-shield-user-line' },
      { name: 'MenuManage', title: '菜单权限管理', icon: 'ri-node-tree' },
      { name: 'UserManage', title: '员工与权限', icon: 'ri-user-settings-line' },
      { name: 'DictManage', title: '字典管理', icon: 'ri-book-2-line' },
      { name: 'SysLog', title: '系统日志', icon: 'ri-history-line' },
    ],
  },
];

export const STUDIO_NAVIGATION_ROUTES = STUDIO_NAVIGATION.flatMap(group =>
  group.children.flatMap(item => [item.name, ...(item.relatedRoutes || [])])
);

export const DESIGN_FLOW_STEPS = STUDIO_NAVIGATION.flatMap(group => group.children)
  .filter(item => item.step)
  .sort((a, b) => Number(a.step) - Number(b.step));

export function navigationForRoute(name: unknown) {
  for (const group of STUDIO_NAVIGATION) {
    const item = group.children.find(item => item.name === name || item.relatedRoutes?.includes(String(name)));
    if (item) return { group, item };
  }
  return null;
}
